import { defineStore } from "pinia"
import { useMainStore } from "@/stores/MainStore"
import getApiUrl from "@/helpers/getApiUrl"
import axios from '@axios'
import getUserType from "@/helpers/getUserType"
import filterHelper from "@/helpers/filterHelper"

export const useUserStore = defineStore("userStore", {
  state: () => {
    return {
      isLoading: false,
      isFormLoading: false,
      reloadList: false,
      isOnboarded: true,
      completeOnboardingSteps: [],
      userRolesArray: [],
    }
  },
  actions: {
    async fetchUsersList() {
      try {
        const response = await axios.get(`api/${ getApiUrl() }/users`)
        
        return response.data.data
      } catch (error) {
        console.error(error)
      }
    },

    async getOnboardingSteps() {
      try {
        const response = await axios.get(`api/${getApiUrl()}/onboarding-steps`)

        return response.data.data
      } catch (error) {
        console.error(error)
      }
    },

    async updateOnboardingSteps(id) {
      try {
        const mainStore = useMainStore()

        if (!this.isOnboarded && !this.completeOnboardingSteps.includes(id)) {
          const response = await axios.patch(`api/${getApiUrl()}/onboarding-steps/?step_id=${id}`)

          if (response) {
            await mainStore.addMessage('success', 'Onboarding Steps Updated!')

            return this.completeOnboardingSteps = response.data?.data?.completed_onboarding_steps
          }
        }

      } catch (error) {
        console.error(error)
      }
    },

    async getUserDetails(id) {
      try {
        const response = await axios.get(`api/${ getApiUrl() }/users/${id}`)
        
        return response.data.data
      } catch (error) {
        console.error(error)
      }
    },

    async getUserRoles() {
      try {
        const response = await axios.get(`api/${ getApiUrl() }/user-roles`)

        this.userRolesArray = response.data
      } catch (error) {
        console.error(error)
      }
    },

    async getUserProfile() {
      try {
        const response = await axios.get(`api/${ getApiUrl() }/profile`)
        if (response) {
          const profile = response.data.data

          this.isOnboarded = profile.is_onboarding_complete
          this.completeOnboardingSteps = profile.completed_onboarding_steps || []

          return profile
        }

      } catch (error) {
        console.error(error)
      }
    },

    async getUserActivity(page, itemsPerPage, filters, sortBy) {
      try {
        let activityURL = `api/${ getApiUrl() }/activity-logs?per_page=${ itemsPerPage }&page=${ page }`
        if (sortBy.length) {
          const sortKey = sortBy[0].key
          const sortOrder = sortBy[0].order

          activityURL = activityURL + `&sort=${sortKey}&sort_dir=${sortOrder.toUpperCase()}`
        }
        activityURL = filters ? filterHelper(activityURL, filters) : activityURL

        const response = await axios.get(activityURL)
        
        return response.data
      } catch (error) {
        console.error(error)
      }
    },

    async editAffiliateProfile(profileData) {
      try {
        const mainStore = useMainStore()
        const formData = new FormData()

        const storefront = profileData?.storefront_theme

        // Append simple fields
        formData.append('_method', 'patch')
        formData.append('name', profileData.name)
        formData.append('base_url', profileData.base_url)
        formData.append('description', profileData.description)
        formData.append('contact_email', profileData.contact_email)
        !profileData.logo || formData.append('logo', profileData.logo?.[0])
        !profileData.icon ||formData.append('icon', profileData.icon?.[0])
        formData.append('show_logo_on_load', profileData.show_logo_on_load)

        // Append nested config object
        formData.append('custom_config[customer_facing_blurb]', profileData.customer_blurb)
        formData.append('custom_config[pages:primary_colour]', profileData.primary_color)
        formData.append('custom_config[pages:theme]', profileData.theme)

        profileData?.pages_type && formData.append('custom_config[pages:type]', profileData.pages_type)
        storefront?.colours?.background_colour && formData.append('custom_config[storefront_theme][colours][background_colour]', storefront.colours.background_colour)
        storefront?.colours?.line_colour && formData.append('custom_config[storefront_theme][colours][line_colour]', storefront.colours.line_colour)
        storefront?.colours?.header_colour && formData.append('custom_config[storefront_theme][colours][header_colour]', storefront.colours.header_colour)
        storefront?.colours?.section_background_colour && formData.append('custom_config[storefront_theme][colours][section_background_colour]', storefront.colours.section_background_colour)
        storefront?.colours?.colour_theme && formData.append('custom_config[storefront_theme][colours][colour_theme]', storefront.colours.colour_theme)
        storefront?.colours?.header_theme && formData.append('custom_config[storefront_theme][colours][header_theme]', storefront.colours.header_theme)

        storefront?.typography?.font_family != null && formData.append('custom_config[storefront_theme][typography][font_family]', storefront.typography.font_family)
        storefront?.typography?.font_weight != null && formData.append('custom_config[storefront_theme][typography][font_weight]', storefront.typography.font_weight)
        storefront?.typography?.font_size != null && formData.append('custom_config[storefront_theme][typography][font_size]', storefront.typography.font_size)

        storefront?.editors_info?.name && formData.append('custom_config[storefront_theme][editors_info][name]', storefront.editors_info.name)
        storefront?.editors_info?.short_description && formData.append('custom_config[storefront_theme][editors_info][short_description]', storefront.editors_info.short_description)
        storefront?.editors_info?.photo_shape != null && formData.append('custom_config[storefront_theme][editors_info][photo_shape]', storefront.editors_info.photo_shape)
        if (storefront?.editors_info?.logo || storefront?.editors_info?.logo === null) formData.append('custom_config[storefront_theme][editors_info][logo]', storefront.editors_info.logo?.[0] || storefront.editors_info.logo)

        if (storefront?.banners?.["header:mobile"] || storefront?.banners?.["header:mobile"] === null) formData.append('custom_config[storefront_theme][banners][header:mobile]', storefront.banners["header:mobile"]?.[0] || storefront.banners["header:mobile"])
        if (storefront?.banners?.["header:desktop"] || storefront?.banners?.["header:desktop"] === null) formData.append('custom_config[storefront_theme][banners][header:desktop]', storefront.banners["header:desktop"]?.[0] || storefront.banners["header:desktop"])

        if (storefront?.promo_data?.banner || storefront?.promo_data?.banner === null) formData.append('custom_config[storefront_theme][promo_data][banner]', storefront.promo_data.banner?.[0] || storefront.promo_data.banner)
        storefront?.promo_data?.link && formData.append('custom_config[storefront_theme][promo_data][link]', storefront.promo_data.link)

        storefront?.menu != null && formData.append('custom_config[storefront_theme][menu]', storefront.menu)

        // Append social media URLs
        formData.append('custom_config[social_media_urls][facebook]', profileData.facebook)
        formData.append('custom_config[social_media_urls][twitter]', profileData.twitter)
        formData.append('custom_config[social_media_urls][instagram]', profileData.instagram)
        formData.append('custom_config[social_media_urls][youtube]', profileData.youtube)

        if (profileData.headerBannerDesktop || profileData.headerBannerDesktop === null) formData.append('custom_config[pages:images:header:desktop]', profileData.headerBannerDesktop?.[0] || profileData.headerBannerDesktop)
        if (profileData.headerBannerMobile || profileData.headerBannerMobile === null)  formData.append('custom_config[pages:images:header:mobile]', profileData.headerBannerMobile?.[0] || profileData.headerBannerMobile)
        if (profileData.footerBannerDesktop || profileData.footerBannerDesktop === null)  formData.append('custom_config[pages:images:footer:desktop]', profileData.footerBannerDesktop?.[0] || profileData.footerBannerDesktop)
        if (profileData.footerBannerMobile || profileData.footerBannerMobile === null) formData.append('custom_config[pages:images:footer:mobile]', profileData.footerBannerMobile?.[0] || profileData.footerBannerMobile)

        const response = await axios.post(`api/${getApiUrl()}/profile`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        await mainStore.addMessage('success', 'Affiliate\'s profile updated successfully')
        
        return response.data
      } catch (error) {
        console.error(error)
      }
    },

    async editRetailerProfile(profileData) {
      try {
        const mainStore = useMainStore()
        const formData = new FormData()

        formData.append('_method', 'patch')
        !profileData.new_logo || formData.append('new_logo', profileData.new_logo?.[0])
        formData.append('company_name', profileData.company_name)
        formData.append('contact_url', profileData.contact_url)
        formData.append('delivery', profileData.delivery)
        formData.append('footer_text', profileData.footer_text)
        formData.append('free_delivery_text', profileData.free_delivery_text)
        formData.append('terms_url', profileData.terms_url)
        formData.append('checkout_theme[primary_colour]', profileData.checkout_theme.primary_colour)
        formData.append('custom_config[shopify:tag]', profileData.order_tags)
        formData.append('website', profileData.website)
        formData.append('description', profileData.description)
        formData.append('product_categories', profileData.product_categories)
        formData.append('partnership_terms', profileData.partnership_terms)

        // formData.append('checkout_theme[secondary_colour]', profileData.checkout_theme.secondary_colour)

        const response = await axios.post(`api/${getApiUrl()}/profile`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })

        await mainStore.addMessage('success', 'Company Settings saved successfully')
        
        return response.data
      } catch (error) {
        console.error(error)
      }
    },

    async addUser(userData) {
      try {
        const mainStore = useMainStore()

        mainStore.successMessage = ''

        const response = await axios.post(`/api/${ getApiUrl() }/users`, {
          name: userData.name,
          enabled: userData.enabled,
          email: userData.email,
          email_subscriptions: userData.email_subscriptions,
          role: userData.role,
        })

        mainStore.addMessage('success', 'User added successfully')
        
        return response.data
      } catch (error) {
        console.error(error)
      }
    },

    async deleteUser(id) {
      try {
        const mainStore = useMainStore()

        mainStore.successMessage = ''

        const response = await axios.delete(`/api/${ getApiUrl() }/users/${id}`)

        mainStore.addMessage('success', 'User deleted successfully')
        
        return response.data
      } catch (error) {
        console.error(error)
      }
    },

    async editUser(userData) {
      try {
        const mainStore = useMainStore()

        mainStore.successMessage = ''

        const response = await axios.patch(`/api/${ getApiUrl() }/users/${ userData.id }`, {
          name: userData.name,
          enabled: userData.enabled,
          email: userData.email,
          role: userData.role,
          email_subscriptions: userData.email_subscriptions,
        })

        mainStore.addMessage('success', 'User information edited successfully')
        
        return response.data
      } catch (error) {
        console.error(error)
      }
    },

    resolveEnabledVariant(statusVal) {
      if (statusVal)
        return {
          status: 'Enabled',
          chip: { color: 'success' },
        }

      return {
        status: 'Disabled',
        chip: { color: 'error' },
      }
    },

    async getAllNotifications(page, scope, readState, itemsPerPage) {
      const { apiVersion } = useMainStore()
      try {
        let notificationsUrl = `api/${ apiVersion }/user/notifications?page=${ page }&per_page=${ itemsPerPage }`
        if (scope) {
          notificationsUrl = notificationsUrl + `&filters[scope]=${ scope }`
        }
        if (readState) {
          notificationsUrl = notificationsUrl + `&filters[read_state]=${ readState }`
        }
        
        return await axios.get(notificationsUrl)
      } catch (error) {
        console.error(error)
      }
    },

    async markNotificationRead(id) {
      try {
        this.reloadList = false

        const mainStore = useMainStore()
        const { apiVersion } = useMainStore()

        try {
          const response = await axios.post(`api/${ apiVersion }/user/notifications/${ id }/read`)

          mainStore.addMessage('success', 'Notification read!')
          this.reloadList = true

          return response.data
        } catch (e) {
          console.error(e)
        }

      } catch (error) {
        console.error(error)
      }
    },

    async markAllNotificationRead() {
      try {
        this.reloadList = false

        const mainStore = useMainStore()
        const { apiVersion } = useMainStore()

        try {
          const response = await axios.post(`api/${ apiVersion }/user/notifications/read/all`)

          mainStore.addMessage('success', 'All notification read!')
          this.reloadList = true

          return response.data
        } catch (e) {
          console.error(e)
        }
      } catch (error) {
        console.error(error)
      }
    },

    async getListOfApprovedIP() {
      const { apiVersion } = useMainStore()

      try {
        const response = await axios.get(`api/${ apiVersion }/user/login-restriction`)
        
        return response.data.data
      } catch (error) {
        console.error(error)
      }
    },

    async updateListOfApprovedIP(addresses) {
      const { apiVersion } = useMainStore()
      const mainStore = useMainStore()

      try {
        const response = await axios.patch(`api/${ apiVersion }/user/login-restriction`, {
          ip_addresses: addresses,
        })

        mainStore.addMessage('success', response.data.message ? response.data.message : 'IP Addresses updated successfully!')

      } catch (error) {
        console.error(error)
      }
    },

    async getDashboardStatistics(scope) {
      try {
        const response = await axios.get(`/api/${ getApiUrl() }/dashboard?date_scope=${ scope }`)
        
        return response.data
      } catch (error) {
        console.error(error)
      }
    },

    async getDashboardMetrics(scope) {
      try {
        const response = await axios.get(`/api/${ getApiUrl() }/metrics?date_scope=${ scope }`)
        
        return response.data
      } catch (error) {
        console.error(error)
      }
    },

    async refreshDashboard() {
      try {
        return await axios.delete(`/api/${ getApiUrl() }/dashboard/clear-cache`)
      } catch (error) {
        console.error(error)
      }
    },
  },
})
